import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { ArrowLeft, Check, Upload, Trash2, GripVertical, X, Plus, Loader2 } from 'lucide-react';
import api from '../utils/api';

// photos 배열 역호환: string → { url, caption }
const norm = p => typeof p === 'string' ? { url: p, caption: '' } : p;

function PhotoDropZone({ onFiles, compact = false }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  const handle = files => {
    const imgs = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imgs.length) onFiles(imgs);
  };
  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files); }}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-lg cursor-pointer transition-colors flex items-center justify-center gap-2
        ${compact ? 'px-3 py-2' : 'p-6 flex-col'}
        ${over ? 'border-brand bg-brand/5' : 'border-gray-700 hover:border-gray-600'}`}
    >
      <Upload size={compact ? 13 : 20} className="text-gray-500" />
      <p className="text-xs text-gray-500">{compact ? '사진 추가' : '드래그하거나 클릭해서 사진 선택 (여러 장 가능)'}</p>
      <input ref={inputRef} type="file" multiple accept="image/*" className="hidden"
        onChange={e => { handle(e.target.files); e.target.value = ''; }} />
    </div>
  );
}

export default function TripReportWritePage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '', destination: '', startDate: '', endDate: '',
    purpose: '', attendees: '', summary: '',
  });
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);

  // 대기 중인 사진: [{ file, preview, caption }]
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const dragBlockRef = useRef(null);
  const dragPhotoRef = useRef(null);
  const blocksRef = useRef(blocks);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/trips/${id}`).then(r => {
      const d = r.data;
      setForm({
        title: d.title || '', destination: d.destination || '',
        startDate: d.startDate || '', endDate: d.endDate || '',
        purpose: d.purpose || '', attendees: d.attendees || '', summary: d.summary || '',
      });
      setBlocks((d.blocks || []).map(b => ({
        id: b.id,
        photos: (b.photos || []).map(norm),
        caption: b.caption || '',
        sortOrder: b.sortOrder,
      })));
    });
  }, [id]);

  const addPendingPhoto = files => {
    setPendingPhotos(prev => [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f), caption: '' }))]);
  };
  const removePendingPhoto = idx => {
    setPendingPhotos(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };
  const updatePendingCaption = (idx, val) => {
    setPendingPhotos(prev => prev.map((p, i) => i === idx ? { ...p, caption: val } : p));
  };

  const confirmBlock = async () => {
    if (pendingPhotos.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      pendingPhotos.forEach(p => fd.append('photos', p.file));
      const r = await api.post('/trips/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const urls = r.data.urls || [];
      const photos = urls.map((url, i) => ({ url, caption: pendingPhotos[i]?.caption || '' }));
      pendingPhotos.forEach(p => URL.revokeObjectURL(p.preview));
      flushSync(() => {
        setBlocks(prev => [...prev, { photos, caption: '', sortOrder: prev.length }]);
        setPendingPhotos([]);
      });
    } catch (e) {
      alert('업로드 실패: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploading(false);
    }
  };

  const onBlockDragStart = (e, idx) => {
    e.dataTransfer.setData('text/plain', String(idx));
    e.dataTransfer.effectAllowed = 'move';
    dragBlockRef.current = idx;
  };
  const onBlockDrop = (e, toIdx) => {
    e.preventDefault();
    const fromIdx = dragBlockRef.current;
    dragBlockRef.current = null;
    if (fromIdx === null || fromIdx === toIdx) return;
    const arr = [...blocksRef.current];
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    flushSync(() => setBlocks(arr));
  };

  const onPhotoDragStart = (e, blockIdx, photoIdx) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    dragPhotoRef.current = { blockIdx, photoIdx };
  };
  const onPhotoDrop = (e, toBlockIdx, toPhotoIdx) => {
    e.stopPropagation();
    e.preventDefault();
    const from = dragPhotoRef.current;
    dragPhotoRef.current = null;
    if (!from || from.blockIdx !== toBlockIdx || from.photoIdx === toPhotoIdx) return;
    const arr = [...blocksRef.current];
    const photos = [...arr[toBlockIdx].photos];
    const [moved] = photos.splice(from.photoIdx, 1);
    photos.splice(toPhotoIdx, 0, moved);
    arr[toBlockIdx] = { ...arr[toBlockIdx], photos };
    flushSync(() => setBlocks(arr));
  };

  const removeBlock = idx => setBlocks(prev => prev.filter((_, i) => i !== idx));
  const removePhoto = (bIdx, pIdx) => setBlocks(prev => prev.map((b, i) =>
    i !== bIdx ? b : { ...b, photos: b.photos.filter((_, j) => j !== pIdx) }
  ));
  const updatePhotoCaption = (bIdx, pIdx, val) => setBlocks(prev => prev.map((b, i) =>
    i !== bIdx ? b : { ...b, photos: b.photos.map((p, j) => j !== pIdx ? p : { ...p, caption: val }) }
  ));

  const addPhotosToBlock = async (bIdx, files) => {
    const imgs = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return;
    const fd = new FormData();
    imgs.forEach(f => fd.append('photos', f));
    try {
      const r = await api.post('/trips/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newPhotos = (r.data.urls || []).map(url => ({ url, caption: '' }));
      flushSync(() => setBlocks(prev => prev.map((b, i) =>
        i !== bIdx ? b : { ...b, photos: [...b.photos, ...newPhotos] }
      )));
    } catch (e) {
      alert('업로드 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  const save = async () => {
    if (!form.title.trim()) { alert('제목을 입력해주세요.'); return; }
    setSaving(true);
    try {
      let reportId = id;
      if (isEdit) {
        await api.put(`/trips/${id}`, form);
        const existing = await api.get(`/trips/${id}`);
        await Promise.all((existing.data.blocks || []).map(b => api.delete(`/trips/${id}/blocks/${b.id}`)));
      } else {
        const r = await api.post('/trips', form);
        reportId = r.data.id;
      }
      for (let i = 0; i < blocks.length; i++) {
        await api.post(`/trips/${reportId}/blocks`, { photos: blocks[i].photos, caption: blocks[i].caption, sortOrder: i });
      }
      navigate(`/trips/${reportId}`);
    } catch (e) {
      alert('저장 실패: ' + (e.response?.data?.message || e.message));
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600';
  const labelCls = 'text-xs text-gray-500 mb-1.5 block';

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <button onClick={() => navigate(isEdit ? `/trips/${id}` : '/trips')}
          className="flex items-center gap-1.5 text-gray-500 hover:text-white text-sm transition-colors">
          <ArrowLeft size={15} /> 출장보고 {isEdit ? '보기' : '목록'}
        </button>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80 disabled:opacity-50">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">

        {/* 좌측: 기본정보 + 전체의견 */}
        <div className="w-80 flex-shrink-0 flex flex-col gap-4 overflow-y-auto">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">출장 정보</h2>
            <div>
              <label className={labelCls}>제목 <span className="text-brand">*</span></label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="예: 2024 부산 박람회 출장" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>목적지</label>
              <input value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))}
                placeholder="예: 부산 벡스코" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>출발일</label>
                <input type="date" value={form.startDate}
                  onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>귀환일</label>
                <input type="date" value={form.endDate}
                  onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>출장인원</label>
              <input value={form.attendees} onChange={e => setForm(f => ({ ...f, attendees: e.target.value }))}
                placeholder="예: 김민준, 이서연" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>출장 목적</label>
              <textarea value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
                rows={2} placeholder="출장 목적을 간략히 적어주세요" className={`${inputCls} resize-none`} />
            </div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex-1 flex flex-col min-h-0">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">전체 의견</h2>
            <textarea value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
              placeholder="출장 전반에 대한 의견, 성과, 느낀 점 등을 자유롭게 적어주세요"
              className={`${inputCls} resize-none flex-1 min-h-0`} style={{ height: '100%' }} />
          </div>
        </div>

        {/* 우측: 사진 블록 */}
        <div className="flex-1 flex flex-col gap-4 overflow-y-auto min-h-0">

          {/* 추가된 블록들 */}
          {blocks.length > 0 && (
            <div className="space-y-3">
              {blocks.map((block, bIdx) => (
                <div key={bIdx} draggable
                  onDragStart={e => onBlockDragStart(e, bIdx)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => onBlockDrop(e, bIdx)}
                  className="bg-gray-900 border border-gray-800 rounded-xl p-4 cursor-grab active:cursor-grabbing"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <GripVertical size={13} className="text-gray-600" />
                    <span className="text-xs text-gray-500">블록 {bIdx + 1}</span>
                    <div className="flex-1" />
                    <button onClick={() => removeBlock(bIdx)} className="p-1 text-gray-600 hover:text-red-400">
                      <Trash2 size={13} />
                    </button>
                  </div>

                  {block.photos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      {block.photos.map((photo, pIdx) => (
                        <div key={pIdx} draggable
                          onDragStart={e => onPhotoDragStart(e, bIdx, pIdx)}
                          onDragOver={e => { e.stopPropagation(); e.preventDefault(); }}
                          onDrop={e => onPhotoDrop(e, bIdx, pIdx)}
                          className="relative group cursor-grab active:cursor-grabbing"
                          onClick={e => e.stopPropagation()}
                        >
                          <img src={photo.url} alt=""
                            className="w-full h-28 object-cover rounded-lg border border-gray-700" />
                          <button onClick={e => { e.stopPropagation(); removePhoto(bIdx, pIdx); }}
                            className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5 opacity-0 group-hover:opacity-100 z-10">
                            <X size={9} className="text-white" />
                          </button>
                          <input
                            value={photo.caption}
                            onChange={e => updatePhotoCaption(bIdx, pIdx, e.target.value)}
                            onClick={e => e.stopPropagation()}
                            onDragStart={e => e.stopPropagation()}
                            placeholder="사진 설명 (선택)"
                            className="mt-1.5 w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-brand placeholder-gray-600"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    value={block.caption}
                    onChange={e => setBlocks(prev => prev.map((b, i) => i === bIdx ? { ...b, caption: e.target.value } : b))}
                    onClick={e => e.stopPropagation()}
                    onDragStart={e => e.stopPropagation()}
                    rows={5}
                    placeholder="블록 전체 설명 (선택)"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600 resize-none mb-3"
                  />
                  <div onClick={e => e.stopPropagation()} onDragStart={e => e.stopPropagation()}>
                    <PhotoDropZone onFiles={files => addPhotosToBlock(bIdx, files)} compact />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 새 블록 추가 */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2">
              <Plus size={13} className="text-brand" /> 사진 블록 추가
            </h2>

            <PhotoDropZone onFiles={addPendingPhoto} />

            {pendingPhotos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {pendingPhotos.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={p.preview} alt=""
                      className="w-full h-28 object-cover rounded-lg border border-gray-700" />
                    <button onClick={() => removePendingPhoto(i)}
                      className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                      <X size={9} className="text-white" />
                    </button>
                    <input
                      value={p.caption}
                      onChange={e => updatePendingCaption(i, e.target.value)}
                      placeholder="사진 설명 (선택)"
                      className="mt-1.5 w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-brand placeholder-gray-600"
                    />
                  </div>
                ))}
              </div>
            )}

            <button onClick={confirmBlock} disabled={uploading || pendingPhotos.length === 0}
              className="w-full flex items-center justify-center gap-2 bg-gray-800 border border-gray-700 text-white text-sm py-2 rounded-lg hover:bg-gray-700 disabled:opacity-40 transition-colors">
              {uploading
                ? <><Loader2 size={13} className="animate-spin" /> 업로드 중...</>
                : <><Check size={13} className="text-brand" /> 블록 추가</>}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
